import { Box, InputAdornment } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import { SystemDropdown } from "../../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { normalizeSelectedJobIds, selectedJobIdsForDisplay } from "../../../../../utils/jobSelection";
import { type AutoApproveRule, type Job, MIN_SHIFT_COVERAGE_OPTIONS, type MinShiftCoverage } from "../../../../../utils/models";
import { AutoApproveDayPresets } from "./AutoApproveDayPresets";
import { AutoApproveRuleDays } from "./AutoApproveRuleDays";
import { AutoApproveRuleField } from "./AutoApproveRuleField";
import { AutoApproveRuleWindows } from "./AutoApproveRuleWindows";
import { AutoApproveRuleZone } from "./AutoApproveRuleZone";
import type { JobCatalogState } from "./autoApproveConsts";
import { approvesNoOne } from "./ruleSummary";

const F = "department.config.staffApp.autoApprove.fields";

type Props = {
	rule: AutoApproveRule;
	index: number;
	jobCatalog: JobCatalogState;
	onChange: (patch: Partial<AutoApproveRule>) => void;
};

// DEFAULT: a job with no display name is shown by its name.
const jobLabelOf = (job: Job): string => job.displayName ?? job.name;

/** "" clears a number field to `null`: the condition is off. */
const numberOrNull = (value: string): number | null => (value === "" ? null : Number(value));

/** A rule's conditions in two groups: which pickups it covers, then when it approves them. */
export const AutoApproveRuleFields: FC<Props> = ({ rule, index, jobCatalog, onChange }) => {
	const { t } = useTranslation();
	const prefix = "dept-config-auto-approve";
	const number = (key: "maxOt" | "timeBeforeShift" | "staffingShortage", step: number, testId: string, unitLabel: string) => (
		<SystemValidationTextField
			inputType="number"
			value={rule[key] != null ? String(rule[key]) : ""}
			onChange={(event) => onChange({ [key]: numberOrNull(event.target.value) })}
			placeholder={t(`${F}.${key}.placeholder`)}
			injectedProps={{ fullWidth: true }}
			// No helper text here, so no reserved line under the field: it would leave it taller than the Jobs dropdown beside it.
			reserveHeightForHelperText={false}
			inputProps={{
				endAdornment: <InputAdornment position="end">{unitLabel}</InputAdornment>,
				inputProps: { min: 0, step, "aria-label": t(`${F}.${key}.label`) },
			}}
			dataTestId={`${prefix}-${testId}-${index}`}
		/>
	);
	const setDays = (shiftDays: string[]) => onChange({ shiftDays });

	return (
		<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) minmax(0, 2fr)" }, columnGap: 2, alignItems: "start" }}>
			<AutoApproveRuleZone title={t(`${F}.group.pickups.title`)} testId={`${prefix}-pickups-${index}`}>
				<AutoApproveRuleField
					label={t(`${F}.days.label`)}
					tip={t(`${F}.days.tip`)}
					testId={`${prefix}-days-${index}`}
					action={<AutoApproveDayPresets shiftDays={rule.shiftDays} onChange={setDays} testIdPrefix={prefix} index={index} />}
				>
					<AutoApproveRuleDays shiftDays={rule.shiftDays} onChange={setDays} dataTestId={`${prefix}-days-${index}`} />
				</AutoApproveRuleField>
				<AutoApproveRuleField label={t(`${F}.hours.label`)} tip={t(`${F}.hours.tip`)} testId={`${prefix}-hours-${index}`}>
					<AutoApproveRuleWindows windows={rule.shiftHours} onChange={(shiftHours) => onChange({ shiftHours })} testIdPrefix={`${prefix}-${index}`} />
				</AutoApproveRuleField>
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
					<AutoApproveRuleField label={t(`${F}.timeBeforeShift.label`)} tip={t(`${F}.timeBeforeShift.tip`)} testId={`${prefix}-time-before-${index}`}>
						{number("timeBeforeShift", 1, "time-before", t(`${F}.unit.hours`))}
					</AutoApproveRuleField>
					{jobCatalog.status !== "none" && (jobCatalog.status !== "ready" || jobCatalog.jobs.length > 0) && (
						<AutoApproveRuleField label={t(`${F}.jobs.label`)} tip={t(`${F}.jobs.tip`)} testId={`${prefix}-jobs-${index}`}>
							{jobCatalog.status === "loading" && (
								<InlineHint tone="neutral" icon={<JumpingDots />} dataTestId={`${prefix}-jobs-loading-${index}`}>
									{t(`${F}.jobs.loading`)}
								</InlineHint>
							)}
							{jobCatalog.status === "error" && (
								<InlineHint
									tone="warning"
									actionPlacement="below"
									action={
										<SystemButton
											buttonVariant="borderless"
											size="small"
											noFullWidthMobile
											onClick={jobCatalog.retry}
											data-testid={`${prefix}-jobs-retry-${index}`}
										>
											{t("retry")}
										</SystemButton>
									}
									dataTestId={`${prefix}-jobs-error-${index}`}
								>
									{t("appearance.category.jobs.load.failed")}
								</InlineHint>
							)}
							{jobCatalog.status === "ready" && (
								<SystemDropdown<string>
									fullWidth
									multiple
									formControlSx={{ width: "100%" }}
									aria-label={t(`${F}.jobs.label`)}
									items={jobCatalog.jobs.map((job) => ({ value: job.id, label: jobLabelOf(job), selectedLabel: jobLabelOf(job) }))}
									selectedItems={selectedJobIdsForDisplay(rule.jobIds, jobCatalog.jobs)}
									// DEFAULT: `undefined` from the helper means every job; a rule stores that as an empty list.
									onChangeMultiple={(selected) => onChange({ jobIds: normalizeSelectedJobIds(selected, jobCatalog.jobs) ?? [] })}
									data-testid={`${prefix}-jobs-${index}`}
								/>
							)}
						</AutoApproveRuleField>
					)}
				</Box>
			</AutoApproveRuleZone>
			<AutoApproveRuleZone tinted title={t(`${F}.group.approve.title`)} testId={`${prefix}-approve-${index}`}>
				<AutoApproveRuleField label={t(`${F}.staffingShortage.label`)} tip={t(`${F}.staffingShortage.tip`)} testId={`${prefix}-staffing-shortage-${index}`}>
					{number("staffingShortage", 1, "staffing-shortage", t(`${F}.unit.people`))}
				</AutoApproveRuleField>
				<AutoApproveRuleField label={t(`${F}.maxOt.label`)} tip={t(`${F}.maxOt.tip`)} testId={`${prefix}-max-ot-${index}`}>
					{number("maxOt", 0.5, "max-ot", t(`${F}.unit.hours`))}
					<InlineHint open={approvesNoOne(rule)} tone="warning" dataTestId={`${prefix}-max-ot-warning-${index}`}>
						{t("department.config.staffApp.autoApprove.list.approves_no_one_tip")}
					</InlineHint>
				</AutoApproveRuleField>
				<AutoApproveRuleField label={t(`${F}.minShiftCoverage.label`)} tip={t(`${F}.minShiftCoverage.tip`)} testId={`${prefix}-min-coverage-${index}`}>
					<SystemDropdown<MinShiftCoverage>
						fullWidth
						formControlSx={{ width: "100%" }}
						ariaLabel={t(`${F}.minShiftCoverage.label`)}
						items={MIN_SHIFT_COVERAGE_OPTIONS.map((percent) => ({ value: percent, label: t(`${F}.minShiftCoverage.option`, { percent }) }))}
						selectedItem={rule.minShiftCoverage}
						// The dropdown can't be cleared, so undefined is never a real choice.
						onChange={(coverage) => {
							if (coverage !== undefined) onChange({ minShiftCoverage: coverage });
						}}
						data-testid={`${prefix}-min-coverage-${index}`}
					/>
				</AutoApproveRuleField>
			</AutoApproveRuleZone>
		</Box>
	);
};
